import { useCallback, useEffect, useRef, useState } from 'react';

const CARDS = [
  {
    kicker: '01 — Daily Overview',
    title: (
      <>
        The whole floor,
        <br />
        at a <em>glance.</em>
      </>
    ),
    body: 'Live revenue, orders, and average ticket — with peak-hour traffic and revenue-by-category, updated in real time. Walk in, and know exactly how service is going.',
    list: [
      'Real-time sales & order telemetry',
      'Peak traffic-hour curves',
      'Revenue split by category',
    ],
    frame: 'admin.plateera / dashboard',
    img: 'admin-dashboard.png',
    alt: 'Daily Overview dashboard',
  },
  {
    kicker: '02 — Menu Architect',
    title: (
      <>
        Design seasonal
        <br />
        <em>experiences.</em>
      </>
    ),
    body: 'Compose menus the way chefs compose dishes — with availability windows, draft concepts, live versioning, and per-restaurant deployment.',
    list: [
      'Draft / live menu states',
      'Day-part & weekday availability',
      'AI optimizer surfacing under-performing items',
    ],
    frame: 'admin.plateera / menus',
    img: 'admin-menu.png',
    alt: 'Menu Architect screen',
  },
  {
    kicker: '03 — Item Management',
    title: (
      <>
        Curate every
        <br />
        <em>offering.</em>
      </>
    ),
    body: 'Manage pricing, descriptions, imagery, and availability for every item across menus. Your catalogue, properly typeset and properly priced.',
    list: [
      'Rich item cards with photography',
      'Per-restaurant pricing & availability',
      'Out-of-stock automation',
    ],
    frame: 'admin.plateera / items',
    img: 'admin-items.png',
    alt: 'Item Management screen',
  },
  {
    kicker: '04 — Item Configurator',
    title: (
      <>
        Configurable down
        <br />
        to the <em>protein.</em>
      </>
    ),
    body: 'Selectable options, min/max selections, per-option pricing. Every modifier, every upsell — exactly how your kitchen actually works.',
    list: [
      'Single-select & multi-select groups',
      'Per-option pricing deltas',
      'Smart upsell suggestions on the guest app',
    ],
    frame: 'admin.plateera / configure',
    img: 'admin-item-config.png',
    alt: 'Item Configurator screen',
  },
  {
    kicker: '05 — Live Order Hub',
    title: (
      <>
        Real-time order
        <br />
        <em>intake.</em>
      </>
    ),
    body: 'New → Active → Completed. Watch the entire kitchen lifecycle in one place, with per-table grouping and full ticket detail on demand.',
    list: [
      'Three-column kanban for ticket state',
      'Special instructions & modifier visibility',
      'Tax, platform fee, and discount breakdown',
    ],
    frame: 'admin.plateera / orders',
    img: 'admin-orders.png',
    alt: 'Live Order Hub screen',
  },
  {
    kicker: '06 — Tables & QR',
    title: (
      <>
        Floor plans become
        <br />
        <em>endpoints.</em>
      </>
    ),
    body: 'Every location gets its own QR — table, counter slot, or car bay. Guests scan, your team sees the order land in the right spot. Every venue, instrumented.',
    list: [
      'Per-section floor mapping',
      'Auto-generated, printable QR codes',
      'Live occupancy & turnover analytics',
    ],
    frame: 'admin.plateera / tables',
    img: 'admin-tables.png',
    alt: 'Table management screen',
  },
  {
    kicker: '07 — Loyalty Program',
    title: (
      <>
        Turn first visits
        <br />
        into <em>regulars.</em>
      </>
    ),
    body: 'Points per order, redemption rates, min-spend rules — tune the whole loyalty economy, then watch enrolled guests, points earned, and active value climb.',
    list: [
      'Configurable points & redemption',
      'Earned vs. redeemed analytics',
      'Card & cash payment eligibility',
    ],
    frame: 'admin.plateera / loyalty',
    img: 'admin-loyalty.png',
    alt: 'Loyalty Program screen',
  },
  {
    kicker: '08 — Discount Campaigns',
    title: (
      <>
        Discounts that
        <br />
        run <em>themselves.</em>
      </>
    ),
    body: 'Schedule a time-boxed percentage discount across your whole menu, one menu, a category, or a hand-picked set of items — with an optional per-order cap.',
    list: [
      'Scoped to menu, category, or specific items',
      'Optional cap on total discount per order',
      'Time-boxed with start and end dates',
    ],
    frame: 'admin.plateera / campaigns',
    img: 'admin-campaigns.png',
    alt: 'Campaigns screen',
  },
];

const PROGRESS_LABELS = [
  'Dashboard',
  'Menus',
  'Items',
  'Configurator',
  'Live Orders',
  'Tables',
  'Loyalty',
  'Discounts',
];

const N = CARDS.length;

export default function Walkthrough() {
  const trackRef = useRef(null);
  const fillRef = useRef(null);
  const [idx, setIdx] = useState(0);

  // Counter / label sync: a card becomes "current" once most of it is inside the track
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;
    const cards = Array.from(track.querySelectorAll('.walk-card'));

    cards.forEach((card) => {
      const img = card.querySelector('img');
      if (img && img.decode) img.decode().catch(() => {});
    });

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.intersectionRatio >= 0.6) {
            const i = cards.indexOf(entry.target);
            if (i >= 0) setIdx(i);
          }
        });
      },
      { root: track, threshold: 0.6 }
    );
    cards.forEach((card) => io.observe(card));

    // Progress fill tracks raw scroll position, not just the snapped index
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        const max = track.scrollWidth - track.clientWidth;
        if (fillRef.current && max > 0) {
          fillRef.current.style.transform = `scaleX(${track.scrollLeft / max})`;
        }
        // The last card's own IntersectionObserver ratio can fall just short
        // of the 0.6 threshold depending on trailing track padding, leaving
        // idx stuck one behind once scrolled all the way — force it here.
        if (max > 0 && track.scrollLeft >= max - 2) {
          setIdx(N - 1);
        }
      });
    };
    track.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      io.disconnect();
      track.removeEventListener('scroll', onScroll);
    };
  }, []);

  const goTo = useCallback((i) => {
    const track = trackRef.current;
    if (!track) return;
    const cards = track.querySelectorAll('.walk-card');
    const target = cards[Math.max(0, Math.min(N - 1, i))];
    if (!target) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollTo({
      left: target.offsetLeft - cards[0].offsetLeft,
      behavior: reduce ? 'auto' : 'smooth',
    });
  }, []);

  return (
    <section className="walk" id="walk">
      <div className="walk-header">
        <div className="walk-header-l">
          <div className="kicker">
            <span className="kicker-dot" />
            <span>Inside the console</span>
          </div>
          <h2 className="display walk-title">
            One canvas
            <br />
            for every <em>operation</em>.
          </h2>
        </div>
        <div className="walk-header-r">
          <p className="lede">
            Eight surfaces your team touches every shift — designed to be calm
            under load, brutal under inspection. Swipe or step through.
          </p>
          <div className="walk-header-meta">
            <span>08 SURFACES</span>
            <span>·</span>
            <span>1 PLATFORM</span>
          </div>
        </div>
      </div>

      <div className="walk-progress">
        <div className="walk-progress-left">
          <span className="walk-progress-num">
            <span className="curr">{String(idx + 1).padStart(2, '0')}</span>{' '}
            <span className="of">/ {String(N).padStart(2, '0')}</span>
          </span>
          <span className="walk-progress-label">
            {PROGRESS_LABELS.map((label, i) => (
              <span
                className={`lbl${i === idx ? ' active' : ''}`}
                style={{ transform: `translateY(${-idx * 100}%)` }}
                key={label}
              >
                {label}
              </span>
            ))}
          </span>
        </div>
        <div className="walk-progress-bar">
          <div className="walk-progress-fill" ref={fillRef} />
        </div>
        <div className="walk-nav">
          <button
            type="button"
            className="walk-nav-btn"
            aria-label="Previous screen"
            disabled={idx === 0}
            onClick={() => goTo(idx - 1)}
          >
            <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
              <path d="M10 3 5 8l5 5" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button
            type="button"
            className="walk-nav-btn"
            aria-label="Next screen"
            disabled={idx === N - 1}
            onClick={() => goTo(idx + 1)}
          >
            <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
              <path d="m6 3 5 5-5 5" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </div>

      <div className="walk-carousel" ref={trackRef}>
        {CARDS.map((card, i) => (
          <article className="walk-card" data-i={i} key={i}>
            <div className="card-text">
              <div className="card-kicker">
                <span className="dot" />
                {card.kicker}
              </div>
              <h3 className="display">{card.title}</h3>
              <p>{card.body}</p>
              <ul className="card-list">
                {card.list.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
            <div className="card-image">
              <div className="screen-frame">
                <div className="frame-bar">
                  <span />
                  <span />
                  <span />
                  <i>{card.frame}</i>
                </div>
                <img src={`${process.env.PUBLIC_URL}/assets/${card.img}`} alt={card.alt} loading="lazy" decoding="async" />
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
